/**
 * METAR / SPECI parser: one airport report's text into the shared `Observation` shape.
 *
 * Pure and I/O-free. It never throws: a report is free text typed or generated at thousands of
 * stations, and one station's garbled group must not take down the panel for every other source.
 * Anything it cannot read is skipped and returned in `unparsed`, so a surprise is visible in tests
 * and logs instead of silently becoming a wrong number.
 *
 * Conventions it applies, beyond those in `types.ts`:
 *
 * - **Only the observed body is read.** Everything after `RMK` (remarks) or a trend marker
 *   (`NOSIG`, `TEMPO`, `BECMG`, used outside the US) is kept as text but never parsed into the
 *   observation. A trend is a forecast: `TEMPO 2000 BR` read as current would invent fog. The one
 *   exception is the US `T` remark (`T01780167`), which restates temperature and dewpoint to a
 *   tenth of a degree and is what NOAA's own decoder uses.
 * - **Calm** (`00000KT`) is wind `0` with direction `null`: a real reading of no wind, not a
 *   missing one. A report with **no wind group at all** leaves wind `null`.
 * - **Visibility is recorded as stated**: `10SM` is 10, `P6SM` is 6, `M1/4SM` is 0.25 (the "less
 *   than" is lost, which only ever errs toward a lower, more cautious number). Metres are
 *   converted and rounded to 0.01 SM, as NOAA does; `9999` (10 km or more) and `CAVOK` become
 *   6.21 SM, the stated 9,999 m.
 * - **Precipitation is "at the station".** It is `true` when any present-weather group contains
 *   drizzle, rain, snow, snow grains, ice crystals, ice pellets, hail, small hail or unknown
 *   precipitation (DZ RA SN SG IC PL GR GS UP), with or without intensity, `SH`, `TS` or `FZ`.
 *   A `VC` ("in the vicinity", 5-10 SM away) group does **not** count: it is not falling on the
 *   landing area, and a jump-run limit on "precipitation present" should not trip on a shower
 *   8 miles off. The group is still returned in `weather`, so a later rule can use it.
 *   Recent weather (`RERA`) is the past hour, not now, and does not count either.
 *   With no precipitation group the answer is `false`, except where the station says it cannot
 *   tell: an `AO1` station has no precipitation sensor, `PWINO` says the sensor is out, and `//`
 *   in the weather position says weather was not observed. Those give `null`, because "no rain
 *   reported" from a station that cannot see rain is not evidence of no rain.
 * - **Ceiling** is the lowest broken, overcast or vertical-visibility layer. `SKC`, `CLR`, `NSC`,
 *   `NCD` and `CAVOK` give `'none'`. A ceiling-type layer whose height was not measured (`BKN///`,
 *   `VV///`) makes the ceiling `null`, never `'none'`: an unknown ceiling is not a clear sky.
 * - **Altimeter** `A2992` is 29.92 inHg; `Q1013` is converted from hectopascals and rounded to
 *   0.01 inHg, the precision an `A` group carries.
 * - **Metres per second** and km/h winds are converted to knots and rounded to the whole knot, as
 *   NOAA does. A decimal would be false precision: 1 m/s is already about 2 kt.
 */

import { emptyObservation, type CloudLayer, type Observation, type UtcMs } from './types.js'

export interface MetarParseOptions {
  /**
   * The time the report is being read, used to work out which month and year its day-of-month
   * group (`011753Z`) belongs to. Pass the fetch time. The report must be within about two weeks
   * of it, which a live feed always is.
   */
  now: UtcMs
  /** The `Observation.sourceId` to stamp. Defaults to the station id, then `'unknown'`. */
  sourceId?: string
}

export interface MetarParseResult {
  observation: Observation
  /** ICAO station id, e.g. `KDEN`. `null` when the report had none we could read. */
  station: string | null
  reportType: 'METAR' | 'SPECI' | null
  /** Fully automated report (`AUTO`). */
  auto: boolean
  /** Corrected report (`COR`, or Canada's `CCA`/`CCB`...). */
  corrected: boolean
  /** `NIL`: the station sent an empty report. Every measurement is then `null`. */
  nil: boolean
  /**
   * Whether the day/time group was read. When it was not, `observation.observedAt` is `0`
   * (1970), deliberately: a report of unknown age must look stale to every staleness check, never
   * fresh. Substituting `now` would make a garbled report look like a current one.
   */
  timeKnown: boolean
  /** Present-weather groups exactly as written, in order, e.g. `['-RA', 'BR']`. */
  weather: string[]
  /** Recent-weather groups (`RERA`), the past hour only. Not current conditions. */
  recentWeather: string[]
  /** The trend forecast appended to many non-US reports (`NOSIG`, `TEMPO ...`). Never parsed. */
  trend: string | null
  /** Everything after `RMK`. Only the `T` temperature group and sensor flags are read from it. */
  remarks: string | null
  /** Groups in the observed body that matched no known form. Skipped, never guessed at. */
  unparsed: string[]
}

// ---------------------------------------------------------------------------------------------
// Shared building blocks. Exported because the TAF parser reads the same wind, visibility,
// weather and cloud groups and must give them identical meaning and null conventions.
// ---------------------------------------------------------------------------------------------

/**
 * Resolve a day-of-month + time (as in `011753Z` or a TAF's `0218/0306`) to UTC epoch ms, using
 * a reference time to supply the month and year the report leaves out.
 *
 * Takes the candidate in the reference month, the two before it and the one after, drops any
 * where the day does not exist (`Date.UTC` would silently roll 31 September into 1 October), and
 * returns the one closest to the reference. "Closest" rather than "latest before" because a TAF's
 * valid period runs up to 30 hours after it was issued, and a clock a few minutes fast must not
 * push a report back a month. Hour `24` is accepted (TAFs write a period's end as `0224`).
 */
export function resolveDayTime(day: number, hour: number, minute: number, reference: UtcMs): UtcMs | null {
  if (!Number.isInteger(day) || day < 1 || day > 31) return null
  if (!Number.isInteger(hour) || hour < 0 || hour > 24) return null
  if (!Number.isInteger(minute) || minute < 0 || minute > 59) return null
  if (hour === 24 && minute !== 0) return null
  if (!Number.isFinite(reference)) return null
  const ref = new Date(reference)
  let best: UtcMs | null = null
  for (const offset of [-2, -1, 0, 1]) {
    const monthStart = new Date(Date.UTC(ref.getUTCFullYear(), ref.getUTCMonth() + offset, 1))
    const y = monthStart.getUTCFullYear()
    const m = monthStart.getUTCMonth()
    const dayCheck = new Date(Date.UTC(y, m, day))
    if (dayCheck.getUTCMonth() !== m) continue // e.g. 31 in a 30-day month
    const t = Date.UTC(y, m, day, hour, minute)
    if (best === null || Math.abs(t - reference) < Math.abs(best - reference)) best = t
  }
  return best
}

/** Knots per metre-per-second. */
const KT_PER_MPS = 1.943844
/** Knots per km/h. */
const KT_PER_KMH = 1 / 1.852
/** Metres per statute mile. */
const M_PER_SM = 1609.344
/** Hectopascals per inch of mercury. */
const HPA_PER_INHG = 33.8639

export function metresToSm(m: number): number {
  return Math.round((m / M_PER_SM) * 100) / 100
}

/** Avoid `-0` leaking out of `M00` (minus zero degrees) and similar. */
function clean(n: number): number {
  return n === 0 ? 0 : n
}

export interface WindGroup {
  windDirDeg: number | null
  windVariable: boolean
  windKt: number | null
  gustKt: number | null
}

const WIND_RE = /^(\d{3}|VRB|\/{3})(P?\d{2,3}|\/\/)(?:G(P?\d{2,3}))?(KT|MPS|KMH)$/

/** Read a wind group (`27015G25KT`, `VRB03KT`, `00000KT`, `05003MPS`, `/////KT`). */
export function parseWindGroup(token: string): WindGroup | null {
  const m = WIND_RE.exec(token)
  if (!m) return null
  const [, dir = '', spd = '', gst, unit = 'KT'] = m
  const toKt = (s: string): number => {
    const v = Number(s.replace('P', ''))
    if (unit === 'MPS') return Math.round(v * KT_PER_MPS)
    if (unit === 'KMH') return Math.round(v * KT_PER_KMH)
    return v
  }
  // `/////KT`: the wind sensor reported nothing. Not calm: unknown.
  const windKt = spd.startsWith('/') ? null : toKt(spd)
  const gustKt = gst === undefined ? null : toKt(gst)
  const windVariable = dir === 'VRB'
  let windDirDeg: number | null = null
  if (!windVariable && !dir.startsWith('/')) {
    const d = Number(dir)
    // `000` with a speed is north written oddly; with no speed it is calm and has no direction.
    if (d === 0) windDirDeg = windKt !== null && windKt > 0 ? 360 : null
    else if (d <= 360) windDirDeg = d
  }
  return { windDirDeg, windVariable, windKt, gustKt }
}

const VIS_SM_RE = /^([MP])?(\d{1,3})SM$/
const VIS_FRACTION_RE = /^([MP])?(\d)\/(\d{1,2})SM$/
const VIS_METRES_RE = /^(\d{4})(NDV)?$/

/**
 * Read a visibility group in statute miles. `next` is the following token, because US reports
 * split a mixed number over two groups (`1 1/2SM`). Returns how many tokens were consumed.
 */
export function parseVisibilityGroup(
  token: string,
  next: string | undefined,
): { visibilitySm: number; consumed: 1 | 2 } | null {
  let m = VIS_SM_RE.exec(token)
  if (m) return { visibilitySm: Number(m[2]), consumed: 1 }
  m = VIS_FRACTION_RE.exec(token)
  if (m) {
    const den = Number(m[3])
    if (den === 0) return null
    return { visibilitySm: Number(m[2]) / den, consumed: 1 }
  }
  if (/^\d$/.test(token) && next !== undefined) {
    const f = /^(\d)\/(\d{1,2})SM$/.exec(next)
    if (f) {
      const den = Number(f[2])
      if (den === 0) return null
      return { visibilitySm: Number(token) + Number(f[1]) / den, consumed: 2 }
    }
  }
  m = VIS_METRES_RE.exec(token)
  if (m) return { visibilitySm: metresToSm(Number(m[1])), consumed: 1 }
  if (token === 'CAVOK') return { visibilitySm: metresToSm(9999), consumed: 1 }
  return null
}

const WX_RE =
  /^(\+|-|VC)?(MI|PR|BC|DR|BL|SH|TS|FZ)?((?:DZ|RA|SN|SG|IC|PL|GR|GS|UP|BR|FG|FU|VA|DU|SA|HZ|PY|PO|SQ|FC|SS|DS)*)$/
const PRECIP_CODES = new Set(['DZ', 'RA', 'SN', 'SG', 'IC', 'PL', 'GR', 'GS', 'UP'])

/**
 * Read a present-weather group (`-RA`, `+TSRA`, `VCSH`, `FZFG`, `-SHRASN`). Returns whether it
 * means precipitation at the station, or `null` if the token is not a weather group.
 */
export function parseWeatherGroup(token: string): { precipitation: boolean } | null {
  const m = WX_RE.exec(token)
  if (!m) return null
  const [, intensity, descriptor, phenomena = ''] = m
  if (descriptor === undefined && phenomena === '') return null
  // Intensity alone with a descriptor but no phenomenon is only valid for TS and SH-in-vicinity.
  if (intensity !== undefined && intensity !== 'VC' && phenomena === '' && descriptor !== 'TS') return null
  const codes = phenomena.match(/.{2}/g) ?? []
  // Drifting / blowing snow (`DRSN`, `BLSN`) is snow lifted off the ground by wind, not falling
  // precipitation; a station reports `SN BLSN` when both are happening.
  const lifted = descriptor === 'DR' || descriptor === 'BL'
  const precipitation = intensity !== 'VC' && !lifted && codes.some((c) => PRECIP_CODES.has(c))
  return { precipitation }
}

export type CloudGroup =
  | { kind: 'layer'; layer: CloudLayer }
  /** A layer whose height was not measured (`BKN///`, `VV///`). Cannot be a `CloudLayer`. */
  | { kind: 'unknown-height'; cover: CloudLayer['cover'] | null; isCeiling: boolean }
  /** `SKC`, `CLR`, `NSC`, `NCD`: the sky was reported and nothing counts as a layer. */
  | { kind: 'clear' }

const CLOUD_RE = /^(FEW|SCT|BKN|OVC|\/{3})(\d{3}|\/{3})(CB|TCU|\/{3})?$/
const VV_RE = /^VV(\d{3}|\/{3})$/

export function parseCloudGroup(token: string): CloudGroup | null {
  if (token === 'SKC' || token === 'CLR' || token === 'NSC' || token === 'NCD') return { kind: 'clear' }
  const v = VV_RE.exec(token)
  if (v) {
    const h = v[1] ?? ''
    if (h.startsWith('/')) return { kind: 'unknown-height', cover: 'VV', isCeiling: true }
    return { kind: 'layer', layer: { cover: 'VV', baseFtAgl: Number(h) * 100 } }
  }
  const m = CLOUD_RE.exec(token)
  if (!m) return null
  const [, coverText = '', h = '', type] = m
  const cover = coverText.startsWith('/') ? null : (coverText as CloudLayer['cover'])
  // An unknown cover (`///015`) may be broken or overcast, so it may be the ceiling.
  const isCeiling = cover === null || cover === 'BKN' || cover === 'OVC'
  if (cover === null || h.startsWith('/')) return { kind: 'unknown-height', cover, isCeiling }
  const layer: CloudLayer = { cover, baseFtAgl: Number(h) * 100 }
  if (type === 'CB' || type === 'TCU') layer.type = type
  return { kind: 'layer', layer }
}

/**
 * Ceiling from a set of layers: the lowest broken, overcast or vertical-visibility base.
 * `unknownCeiling` is set when a ceiling-type layer had no measured height; the ceiling is then
 * unknown (`null`) rather than whatever the known layers say, since the unknown one may be lower.
 */
export function ceilingFrom(clouds: CloudLayer[] | null, unknownCeiling: boolean): number | 'none' | null {
  if (clouds === null) return null
  if (unknownCeiling) return null
  let lowest: number | null = null
  for (const l of clouds) {
    if (l.cover === 'BKN' || l.cover === 'OVC' || l.cover === 'VV') {
      if (lowest === null || l.baseFtAgl < lowest) lowest = l.baseFtAgl
    }
  }
  return lowest ?? 'none'
}

/** Split report text into groups: whitespace-separated, `\r` and the `=` terminator removed. */
export function tokenize(text: string): string[] {
  return text
    .replace(/\r/g, '')
    .split(/\s+/)
    .map((t) => t.trim())
    .filter((t) => t !== '')
    .map((t, i, all) => (i === all.length - 1 ? t.replace(/=+$/, '') : t))
    .filter((t) => t !== '')
}

// ---------------------------------------------------------------------------------------------
// The METAR parser itself.
// ---------------------------------------------------------------------------------------------

const STATION_RE = /^[A-Z][A-Z0-9]{3}$/
const TIME_RE = /^(\d{2})(\d{2})(\d{2})Z$/
const VAR_DIR_RE = /^(\d{3})V(\d{3})$/
const TEMP_RE = /^(M?\d{2}|\/\/)\/(M?\d{2}|\/\/)?$/
const ALT_A_RE = /^A(\d{4}|\/{4})$/
const ALT_Q_RE = /^Q(\d{4}|\/{4})$/
/** Runway visual range and runway-state groups (`R34/P2000U`, `R06C/CLRD62`, `R88/190050`). */
const RUNWAY_RE = /^R\d{1,2}[LCR]?\/\S+$/
/** Minimum visibility in one direction, after the prevailing visibility (`0150SE`). */
const DIR_VIS_RE = /^\d{4}(N|NE|E|SE|S|SW|W|NW)$/
/** Sea-surface temperature and state (`W15/S2`). */
const SEA_RE = /^W(M?\d{2}|\/\/)\/(S[\d/]|H[\d/]+)$/
/** Military airfield colour state (`BLU`, `WHT`, `BLACKAMB`), a summary of other groups. */
const COLOUR_RE = /^(BLACK)?(BLU|WHT|GRN|YLO\d?|AMB|RED)\+?$/
/** Old 8-digit runway-state group. */
const RUNWAY_STATE_8_RE = /^\d{8}$/
const REMARK_T_RE = /^T([01])(\d{3})(?:([01])(\d{3}))?$/

function parseTemp(s: string | undefined): number | null {
  if (s === undefined || s.startsWith('/')) return null
  const v = s.startsWith('M') ? -Number(s.slice(1)) : Number(s)
  return Number.isFinite(v) ? clean(v) : null
}

/**
 * Order of the body's groups. Used so an ambiguous group is only read where it can legally
 * appear: a 4-digit number after the sky groups is garbage, not a visibility of 1,234 m.
 */
const Stage = { Wind: 0, Visibility: 1, Weather: 2, Sky: 3, Temp: 4, Pressure: 5 } as const

/** Header words that look like a 4-letter station id but are not one. */
const NOT_A_STATION = new Set(['AUTO', 'NIL', 'COR', 'METAR', 'SPECI', 'RMK', 'TEMPO', 'BECMG', 'NOSIG'])

export function parseMetar(text: string, options: MetarParseOptions): MetarParseResult {
  const tokens = typeof text === 'string' ? tokenize(text) : []
  const result: MetarParseResult = {
    observation: emptyObservation(options.sourceId ?? 'unknown', 0),
    station: null,
    reportType: null,
    auto: false,
    corrected: false,
    nil: false,
    timeKnown: false,
    weather: [],
    recentWeather: [],
    trend: null,
    remarks: null,
    unparsed: [],
  }
  const obs = result.observation
  obs.raw = typeof text === 'string' ? text.replace(/\r/g, '').trim() : null

  let i = 0
  // Header: [METAR|SPECI] [COR] STATION DDHHMMZ
  if (tokens[i] === 'METAR' || tokens[i] === 'SPECI') {
    result.reportType = tokens[i] as 'METAR' | 'SPECI'
    i++
  }
  if (tokens[i] === 'COR') {
    result.corrected = true
    i++
  }
  const maybeStation = tokens[i]
  if (
    maybeStation !== undefined &&
    STATION_RE.test(maybeStation) &&
    !NOT_A_STATION.has(maybeStation) &&
    parseWeatherGroup(maybeStation) === null &&
    parseCloudGroup(maybeStation) === null
  ) {
    result.station = maybeStation
    i++
  }
  const timeTok = tokens[i]
  const tm = timeTok === undefined ? null : TIME_RE.exec(timeTok)
  if (tm) {
    const t = resolveDayTime(Number(tm[1]), Number(tm[2]), Number(tm[3]), options.now)
    if (t !== null) {
      obs.observedAt = t
      result.timeKnown = true
    } else {
      result.unparsed.push(tm[0])
    }
    i++
  }
  if (options.sourceId === undefined && result.station !== null) obs.sourceId = result.station

  let stage: number = Stage.Wind
  let windSeen = false
  // Whether any measurement group was read. Text with none is not a report, and its
  // precipitation stays unknown rather than becoming a confident "none".
  let bodySeen = false
  let unknownCeiling = false
  let weatherUnobserved = false
  let cavok = false
  let precip = false

  for (; i < tokens.length; i++) {
    const tok = tokens[i] ?? ''
    if (tok === 'RMK') {
      result.remarks = tokens.slice(i + 1).join(' ')
      break
    }
    if (tok === 'NOSIG' || tok === 'TEMPO' || tok === 'BECMG') {
      const rmk = tokens.indexOf('RMK', i)
      result.trend = tokens.slice(i, rmk === -1 ? undefined : rmk).join(' ')
      if (rmk !== -1) result.remarks = tokens.slice(rmk + 1).join(' ')
      break
    }
    if (tok === 'AUTO') {
      result.auto = true
      continue
    }
    if (tok === 'COR' || /^CC[A-Z]$/.test(tok)) {
      result.corrected = true
      continue
    }
    if (tok === 'NIL') {
      result.nil = true
      continue
    }
    if (tok === 'RTD' || tok === '$') continue

    const wind = !windSeen && stage <= Stage.Wind ? parseWindGroup(tok) : null
    if (wind) {
      windSeen = true
      bodySeen = true
      obs.windDirDeg = wind.windDirDeg
      obs.windVariable = wind.windVariable
      obs.windKt = wind.windKt
      obs.gustKt = wind.gustKt
      stage = Stage.Wind
      continue
    }
    const vd = VAR_DIR_RE.exec(tok)
    if (vd && stage <= Stage.Visibility) {
      obs.windVarFromDeg = Number(vd[1])
      obs.windVarToDeg = Number(vd[2])
      continue
    }
    if (obs.visibilitySm === null && stage <= Stage.Visibility) {
      const vis = parseVisibilityGroup(tok, tokens[i + 1])
      if (vis) {
        obs.visibilitySm = vis.visibilitySm
        bodySeen = true
        if (tok === 'CAVOK') {
          // Ceiling and visibility OK: 10 km or more, no cloud below 5,000 ft or the highest
          // minimum sector altitude, no cumulonimbus, and no significant weather.
          cavok = true
          obs.clouds = []
          stage = Stage.Sky
        } else {
          stage = Stage.Visibility
        }
        i += vis.consumed - 1
        continue
      }
    }
    if (tok === '////' && stage <= Stage.Visibility) {
      stage = Stage.Visibility // visibility not measured
      continue
    }
    if (DIR_VIS_RE.test(tok) && stage <= Stage.Visibility) continue
    // Minimum visibility with no direction, which automatic European stations add after the
    // prevailing one (`9999 1800`). Never the prevailing visibility, so it must not replace it.
    if (stage === Stage.Visibility && /^\d{4}$/.test(tok)) continue
    if (RUNWAY_RE.test(tok) || RUNWAY_STATE_8_RE.test(tok)) continue
    if (tok === '//') {
      weatherUnobserved = true
      stage = Math.max(stage, Stage.Weather)
      continue
    }
    if (tok.startsWith('RE') && tok.length > 2) {
      const wx = parseWeatherGroup(tok.slice(2))
      if (wx) {
        result.recentWeather.push(tok)
        continue
      }
    }
    const wx = stage <= Stage.Sky ? parseWeatherGroup(tok) : null
    if (wx) {
      result.weather.push(tok)
      bodySeen = true
      if (wx.precipitation) precip = true
      stage = Math.max(stage, Stage.Weather)
      continue
    }
    const cloud = stage <= Stage.Sky ? parseCloudGroup(tok) : null
    if (cloud) {
      bodySeen = true
      if (cloud.kind === 'unknown-height') {
        // A layer we cannot place (`FEW///CB`, `VV///`). `CloudLayer` needs a height, so it is
        // left out, and it does not on its own make `clouds` an empty list: "no layers" would
        // read as a clear sky when there is, say, a cumulonimbus of unknown height. If it could
        // be a ceiling, the ceiling is unknown.
        if (cloud.isCeiling) unknownCeiling = true
      } else {
        if (obs.clouds === null) obs.clouds = []
        if (cloud.kind === 'layer') obs.clouds.push(cloud.layer)
      }
      stage = Stage.Sky
      continue
    }
    const t = TEMP_RE.exec(tok)
    if (t && stage <= Stage.Temp) {
      obs.tempC = parseTemp(t[1])
      obs.dewpointC = parseTemp(t[2])
      bodySeen = true
      stage = Stage.Temp
      continue
    }
    const a = ALT_A_RE.exec(tok)
    if (a) {
      const v = a[1] ?? ''
      if (!v.startsWith('/')) obs.altimeterInHg = Number(v) / 100
      bodySeen = true
      stage = Stage.Pressure
      continue
    }
    const q = ALT_Q_RE.exec(tok)
    if (q) {
      const v = q[1] ?? ''
      if (!v.startsWith('/') && obs.altimeterInHg === null) {
        obs.altimeterInHg = Math.round((Number(v) / HPA_PER_INHG) * 100) / 100
      }
      bodySeen = true
      stage = Stage.Pressure
      continue
    }
    if (tok === 'WS') {
      // Wind shear: `WS R07`, `WS RWY07`, `WS ALL RWY`. Consume the runway designator(s).
      while (/^(R\d{2}[LCR]?|RWY\d{2}[LCR]?|ALL|RWY)$/.test(tokens[i + 1] ?? '')) i++
      continue
    }
    if (SEA_RE.test(tok) || COLOUR_RE.test(tok)) continue
    result.unparsed.push(tok)
  }

  // Remarks: the tenths-precision temperature group, and whether the station can see precipitation.
  let noPrecipSensor = false
  if (result.remarks !== null) {
    for (const r of result.remarks.split(' ')) {
      const rt = REMARK_T_RE.exec(r)
      if (rt) {
        const tv = Number(rt[2]) / 10
        obs.tempC = clean(rt[1] === '1' ? -tv : tv)
        if (rt[3] !== undefined && rt[4] !== undefined) {
          const dv = Number(rt[4]) / 10
          obs.dewpointC = clean(rt[3] === '1' ? -dv : dv)
        }
      }
      // `A01` is how some offshore platforms write AO1; NOAA's feed carries both.
      if (r === 'AO1' || r === 'A01' || r === 'PWINO') noPrecipSensor = true
    }
  }

  if (result.nil) {
    // A NIL report has no measurements, whatever else the text holds.
    const keep = { sourceId: obs.sourceId, observedAt: obs.observedAt, raw: obs.raw }
    result.observation = { ...emptyObservation(keep.sourceId, keep.observedAt), raw: keep.raw }
    return result
  }

  obs.ceilingFtAgl = cavok ? 'none' : ceilingFrom(obs.clouds, unknownCeiling)
  if (precip) obs.precipitation = true
  else if (cavok) obs.precipitation = false
  else if (weatherUnobserved || noPrecipSensor) obs.precipitation = null
  else if (bodySeen) obs.precipitation = false
  return result
}
